/**
 * A step's settings, in words. Pure, so the wording is tested once and every
 * place that shows a step says the same thing.
 */

import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import type { ApprovalStep } from './approvalsData';

interface Named { id: string; name: string }

type SummaryStep = Pick<ApprovalStep, 'roleId' | 'assigneeUids' | 'departmentOnly' | 'requiredApprovals' | 'isCommittee' | 'dueDays'>;

/** The people a step names, spelled out. Empty when it names nobody. */
export function namedPeople(step: Pick<ApprovalStep, 'assigneeUids'>, people: readonly Named[]): string[] {
  return (step.assigneeUids ?? []).map((u) => people.find((p) => p.id === u)?.name ?? 'someone no longer on staff');
}

/**
 * The one line under a step's name:
 * "Approver · 5 named people · 3 approvals required · Due in 5 days", or for a
 * committee "Committee decision · recorded by Approver".
 *
 * A COMMITTEE'S DECISION IS THE COMMITTEE'S. The role and the named people on
 * such a step say who may sit at a screen and write down what the committee
 * resolved, with the date it met; they are not the deciders, and the committee
 * itself has no accounts here and needs none. Leading with the role would say
 * the opposite, so a committee step leads with the decision and names the
 * recorder second.
 *
 * ON AN ORDINARY STEP THE ROLE IS THE ROLE THE STEP IS SET TO, always first.
 * At decision time an approver may also decide a reviewer's step and an
 * administrator may decide any step; repeating that on every row would bury
 * what the administrator actually chose, and it is said once under "Rules for
 * this workflow".
 *
 * A DEFAULT IS NEVER SPELLED OUT. "1 approval required" on an ordinary step is
 * noise, and "0 named people" is a lie; both are left out, so what is on the
 * line is what somebody deliberately set.
 *
 * Named people and "the document's own department" cannot both apply: when a
 * step names people, they hold it and the role's reach no longer decides
 * anything, so saying the department narrowed it would be wrong.
 */
export function stepSummary(step: SummaryStep, people: readonly Named[]): string[] {
  const out: string[] = [];
  const named = namedPeople(step, people).length;
  const need = step.requiredApprovals ?? 1;
  const role = step.roleId && isRole(step.roleId) ? ROLE_DISPLAY_NAMES[step.roleId] : null;
  const count = named === 1 ? '1 named person' : `${named} named people`;

  if (step.isCommittee) {
    out.push('Committee decision');
    const recorder = named > 0 ? count : role;
    if (recorder) out.push(`recorded by ${recorder}`);
    else out.push('No role chosen');
  } else {
    out.push(role ?? 'No role chosen');
    if (named > 0) out.push(count);
  }

  if (named === 0 && step.departmentOnly) out.push("The document's own department");
  if (need > 1) out.push(`${need} approvals required`);
  if (step.dueDays) out.push(step.dueDays === 1 ? 'Due in 1 day' : `Due in ${step.dueDays} days`);
  return out;
}

/**
 * Whether a step runs beside the one above it. Its own badge rather than a
 * word on the line above, because it changes the STAGE: two steps sharing a
 * number are asked at once, and an administrator reading the list has to see
 * that without counting.
 */
export function runsWithPrevious(step: Pick<ApprovalStep, 'parallelWithPrevious'>, index: number): boolean {
  return index > 0 && Boolean(step.parallelWithPrevious);
}

/** The number shown beside each step: steps that run together share one. */
export function stageNumbers(steps: readonly Pick<ApprovalStep, 'parallelWithPrevious'>[]): number[] {
  let stage = 0;
  return steps.map((s, i) => (stage = i > 0 && s.parallelWithPrevious ? stage : stage + 1));
}

/** "Due in 3 days", "Due today", "2 days past its deadline", or null when there is no deadline. */
export function dueText(dueAt: string | null | undefined, now: Date = new Date()): { text: string; late: boolean } | null {
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (Number.isNaN(due)) return null;
  const days = Math.floor((due - now.getTime()) / 864e5);
  if (days > 0) return { text: days === 1 ? 'Due tomorrow' : `Due in ${days} days`, late: false };
  if (days === 0) return { text: 'Due today', late: false };
  const over = -days;
  return { text: over === 1 ? '1 day past its deadline' : `${over} days past its deadline`, late: true };
}
